<template>
    <div id="coze-chat-container">
      <!-- 自定义可拖拽悬浮球 -->
      <div 
        class="coze-custom-float-btn"
        :style="floatBtnStyle"
        @mousedown="handleDragStart"
        @click="toggleChatWindow"
        :class="{ 'active': isChatOpen }"
      >
        <i class="coze-float-icon"></i>
        <span class="float-tooltip">需要帮助吗？我是智能小助手~</span>
      </div>
    </div>
  </template>
  
  <script>
  import { mapState } from 'vuex';
  import home from "../api/home";
  
  export default {
  name: 'CozeChat',
  data() {
    return {
      chatClient: null,
      scriptLoaded: false,
      currentToken: null,
      currentUserId: null,
      // 悬浮球配置（初始cursor为pointer）
      floatBtnStyle: {
        position: 'fixed',
        right: '30px',
        bottom: '30px',
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        background: '#1890ff',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer', // 初始：手指
        zIndex: 99999,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        willChange: 'left, top',
        touchAction: 'none'
      },
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      isChatOpen: false,
      // 新增：保存观察者实例
      sendButtonObserver: null,
      // 新增：保存已绑定的事件处理函数（方便移除）
      clickHandler: null,
      keydownHandler: null
    };
  },
  computed: {
    ...mapState(['User']),
    cozeUserInfo() {
      const user = this.User;
      const userId = this.currentUserId || user?.id || user?.mobile || 'guest_' + Math.random().toString(36).substr(2, 9);
      return {
        id: userId,
        nickname: user?.name || user?.mobile || '访客',
        avatar: user?.avatarUrl || 'https://lf-coze-web-cdn.coze.cn/obj/coze-web-cn/obric/coze/favicon.1970.png'
      };
    }
  },
  mounted() {
    // 初始化事件处理函数（绑定this）
    this.clickHandler = () => {
      console.log('🎯 发送按钮被点击！');
      this.handleMessageSent();
    };
    
    this.keydownHandler = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        console.log('⌨️ 用户按下了 Enter 键发送消息！');
        setTimeout(() => {
          this.handleMessageSent();
        }, 50);
      }
    };
  
    if (!this.User?.id && !this.User?.mobile) {
      console.warn('用户未登录，将使用访客模式生成 Token');
    }
    this.loadFloatBtnPosition();
    this.fetchTokenFromBackend()
      .then(() => {
        this.loadCozeScript();
      })
      .catch(err => {
        console.error('❌ 获取 Coze Token 失败:', err);
      });
  },
  beforeDestroy() {
    this.saveFloatBtnPosition();
    document.removeEventListener('mousemove', this.handleDragging);
    document.removeEventListener('mouseup', this.handleDragEnd);
    
    // 销毁时停止观察并清理事件
    this.stopObservingSendButton();
    this.removeAllEventListeners();
  },
  methods: {
    // 新增：启动观察发送按钮
    startObservingSendButton() {
      if (this.sendButtonObserver) return;
      
      console.log('🔍 开始观察发送按钮...');
      this.sendButtonObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const button = node.querySelector('[data-testid="bot-home-chart-send-button"]');
              if (button && !button.dataset.hasClickHandler) {
                button.dataset.hasClickHandler = 'true';
                button.addEventListener('click', this.clickHandler);
              }
              
              const inputArea = node.querySelector('.coze-chat-sdk-input-editor') || 
                               node.querySelector('[contenteditable="true"]') ||
                               node.querySelector('textarea');
              if (inputArea && !inputArea.dataset.hasEnterListener) {
                inputArea.dataset.hasEnterListener = 'true';
                inputArea.addEventListener('keydown', this.keydownHandler);
              }
            }
          });
        });
      });
  
      this.sendButtonObserver.observe(document.body, { childList: true, subtree: true });
    },
    
    // 新增：停止观察发送按钮
    stopObservingSendButton() {
      if (this.sendButtonObserver) {
        console.log('🛑 停止观察发送按钮');
        this.sendButtonObserver.disconnect();
        this.sendButtonObserver = null;
      }
    },
    
    // 新增：移除所有已绑定的事件监听
    removeAllEventListeners() {
      // 移除发送按钮点击事件
      const buttons = document.querySelectorAll('[data-testid="bot-home-chart-send-button"]');
      buttons.forEach(button => {
        if (button.dataset.hasClickHandler) {
          button.removeEventListener('click', this.clickHandler);
          delete button.dataset.hasClickHandler;
        }
      });
      
      // 移除输入框回车事件
      const inputAreas = document.querySelectorAll(
        '.coze-chat-sdk-input-editor, [contenteditable="true"], textarea'
      );
      inputAreas.forEach(input => {
        if (input.dataset.hasEnterListener) {
          input.removeEventListener('keydown', this.keydownHandler);
          delete input.dataset.hasEnterListener;
        }
      });
    },
  
    // 2. 修改 Vue 组件中的 handleMessageSent 方法
    async handleMessageSent() {
      try {
        // 第一步：校验对话次数
        console.log('🔍 校验用户对话次数限制...');
        const checkRes = await home.checkConversationLimit();
        if (!checkRes.success) {
          throw new Error(checkRes.msg || '校验对话次数失败');
        }
  
        // 第二步：判断是否超出上限
        if (checkRes.isExceed) {
          // 超出限制，提示用户并阻止发送
          this.$message({
            type: 'error',
            message: `今日对话次数已达上限（${checkRes.maxCount}次），请明日再试！`,
            duration: 5000
          });
          console.warn(`❌ 用户${checkRes.userId}超出对话上限：${checkRes.currentCount}/${checkRes.maxCount}`);
          // 阻止Coze发送消息（核心：模拟点击取消或清空输入）
          const inputArea = document.querySelector('.coze-chat-sdk-input-editor') || 
                            document.querySelector('[contenteditable="true"]') ||
                            document.querySelector('textarea');
          if (inputArea) {
            inputArea.innerHTML = ''; // 清空输入框
            inputArea.value = '';     // 兼容textarea
          }
          this.chatClient.hideChatBot();//关闭窗口禁止打开
          return; // 终止后续逻辑
        }
  
        // 第三步：未超出限制，正常发送并递增计数
        console.log(`✅ 用户${checkRes.userId}对话次数：${checkRes.currentCount}/${checkRes.maxCount}`);
        console.log('✅ 捕获到消息发送事件（点击或回车）');
        console.log('📊 发送时间:', new Date().toLocaleString());
  
        // 递增对话次数（异步，不阻塞发送）
        home.incrementConversationCount({userId:checkRes.userId})
          .then(res => {
            if (res.success) {
              console.log(`📈 对话次数递增成功，当前：${res.currentCount}/${res.maxCount}`);
            } else {
              console.error('❌ 递增对话次数失败:', res.msg);
            }
          })
          .catch(err => {
            console.error('❌ 递增对话次数异常:', err);
          });
  
      } catch (err) {
        console.error('❌ 处理消息发送事件失败:', err);
        this.$message({
          type: 'error',
          message: '发送失败：' + err.message,
          duration: 3000
        });
      }
    },
  
    // 原有 Token 相关逻辑（不变）
    fetchTokenFromBackend() {
      return new Promise((resolve, reject) => {
        home.getCozeAccessToken()
          .then((res) => {
            let token = '';
            if (res && typeof res === 'object') {
              if (res.success) {
                token = res.data;
                this.currentUserId = res.user_id || this.cozeUserInfo.id;
              } else {
                reject(new Error(res.msg || '获取 Token 失败'));
                return;
              }
            } else if (typeof res === 'string') {
              token = res;
            }
  
            if (!token) {
              console.error('❌ 后端返回的 Access Token 为空:', res);
              reject(new Error('Token is empty'));
              return;
            }
  
            this.currentToken = token;
            window.COZE_CURRENT_TOKEN = token;
            console.log(`✅ Access Token 获取成功 (用户: ${this.cozeUserInfo.nickname}):`, token.substring(0, 15) + '...');
            resolve();
          })
          .catch(err => {
            console.error('❌ 请求 Access Token 失败:', err);
            reject(err);
          });
      });
    },
  
    loadCozeScript() {
      if (window.CozeWebSDK) {
        this.scriptLoaded = true;
        this.initChat();
      } else {
        const script = document.createElement('script');
        script.src = 'https://lf-cdn.coze.cn/obj/unpkg/flow-platform/chat-app-sdk/1.2.0-beta.19/libs/cn/index.js';
        script.crossOrigin = true;
        script.onload = () => {
          this.scriptLoaded = true;
          this.initChat();
        };
        script.onerror = () => {
          console.error('Coze SDK 加载失败');
        };
        document.body.appendChild(script);
      }
    },
  
    initChat() {
      if (!window.CozeWebSDK || !this.currentToken) return;
  
      const userInfo = this.cozeUserInfo;
      const botId = '7631922855441416228';
  
      console.log(`✅ 初始化聊天窗口 (用户: ${userInfo.nickname}, ID: ${userInfo.id})`);
  
      this.chatClient = new window.CozeWebSDK.WebChatClient({
        config: {
          bot_id: botId,
          isIframe: false,
        },
        componentProps: {
          title: '易企软件AI助手（测试版）',
        },
        auth: {
          type: 'token',
          token: this.currentToken,
          onRefreshToken: () => {
            console.log('⚠️ Token 即将过期，SDK 触发刷新...');
            return this.fetchTokenFromBackend()
              .then(() => {
                const newToken = this.currentToken;
                if (newToken) {
                  console.log('✅ Access Token 刷新成功');
                  return newToken;
                } else {
                  throw new Error('Refreshed token is empty');
                }
              })
              .catch(err => {
                console.error('❌ Token 刷新失败:', err);
                throw err;
              });
          }
        },
        userInfo: {
          id: userInfo.id,
          url: userInfo.avatar,
          nickname: userInfo.nickname,
        },
        ui: {
          asstBtn: {
            isNeed: false // 隐藏SDK默认悬浮球
          },
          base: {
            icon: userInfo.avatar,
            layout: 'pc',
            zIndex: 9999,
            lang:'zh-CN',
          },
          footer: {
            isShow: true,
            expressionText: 'Powered by 易企软件|AI回答不代表易企软件',
          },
          conversations: {
            isNeed: true
          },
          chatBot: {
            uploadable: true,
            width: 390,
            isNeedAudio: false,
            isNeedFunctionCallMessage: true,
            isNeedAddNewConversation: true,
            isNeedQuote: true,
            onHide: () => {
              console.log('聊天框已隐藏');
              // 聊天窗口隐藏时停止观察并清理事件
              this.stopObservingSendButton();
              this.removeAllEventListeners();
              this.isChatOpen = false;
            },
            onShow: () => {
              console.log('聊天框已显示');
              // 聊天窗口显示时启动观察
              this.startObservingSendButton();
              this.isChatOpen = true;
            },
          }
        },
      });
    },
  
    // 拖拽相关（核心修改）
    handleDragStart(e) {
      // 只允许鼠标左键拖拽
      if (e.button !== 0) return;
  
      this.isDragging = false;
      this.dragMoved = false;
  
      const btn = e.target.closest('.coze-custom-float-btn');
      if (!btn) return;
  
      this.floatBtnStyle.cursor = 'move';
  
      const rect = btn.getBoundingClientRect();
      this.dragStartX = e.clientX - rect.left;
      this.dragStartY = e.clientY - rect.top;
  
      document.addEventListener('mousemove', this.handleDragging);
      document.addEventListener('mouseup', this.handleDragEnd);
  
      // 关键：阻止事件冒泡到外层，但不阻止默认行为
      e.stopPropagation();
    },
  
    handleDragging(e) {
      // 阻止默认行为 + 阻止冒泡，最关键
      e.preventDefault();
      e.stopPropagation();
  
      this.isDragging = true;
      this.dragMoved = true; // 👈 加这一行
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const sizeNum = parseInt(this.floatBtnStyle.width);
  
      let newLeft = e.clientX - this.dragStartX;
      let newTop = e.clientY - this.dragStartY;
      
      // 边界限制
      newLeft = Math.max(0, Math.min(newLeft, windowWidth - sizeNum));
      newTop = Math.max(0, Math.min(newTop, windowHeight - sizeNum));
  
      // 只改位置，不改 cursor
      this.floatBtnStyle.left = `${newLeft}px`;
      this.floatBtnStyle.top = `${newTop}px`;
      this.floatBtnStyle.right = 'auto';
      this.floatBtnStyle.bottom = 'auto';
    },
  
    handleDragEnd() {
      this.isDragging = false;
      this.floatBtnStyle.cursor = 'pointer'; // 👈 直接改，不加延时
      
      document.removeEventListener('mousemove', this.handleDragging);
      document.removeEventListener('mouseup', this.handleDragEnd);
      this.saveFloatBtnPosition();
    },
  
    // 切换聊天窗口
    async toggleChatWindow() {
      if (this.dragMoved) { // 👈 加这个判断
        this.dragMoved = false;
        return;
      }
      if (this.isDragging) return;
      if (!this.chatClient) {
        console.warn('聊天窗口尚未初始化');
        return;
      }
      const checkRes = await home.checkConversationLimit();
      if (!checkRes.success) {
        this.$message.error(checkRes.msg || '校验对话次数失败');
        return;
      }
  
      // 第二步：判断是否超出上限
      if (checkRes.isExceed) {
        this.$message.error('对话次数已达上限，请明天再试吧~。');
        return;
      }
  
      if (this.isChatOpen) {
        this.chatClient.hideChatBot();
      } else {
        this.chatClient.showChatBot();
      }
    },
  
    // 保存位置（排除cursor）
    saveFloatBtnPosition() {
      const { left, top, width, height, background } = this.floatBtnStyle;
      localStorage.setItem('cozeFloatBtnConfig', JSON.stringify({
        left, top, width, height, background
      }));
    },
  
    loadFloatBtnPosition() {
      const savedConfig = localStorage.getItem('cozeFloatBtnConfig');
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          this.floatBtnStyle = { ...this.floatBtnStyle, ...config };
        } catch (e) {
          console.warn('加载悬浮球位置失败:', e);
        }
      }
    }
  },
  watch: {
    User: {
      handler(newVal) {
        if (this.scriptLoaded && newVal) {
          if (this.chatClient && this.chatClient.updateUserInfo) {
            const userInfo = this.cozeUserInfo;
            this.chatClient.updateUserInfo({
              id: userInfo.id,
              url: userInfo.avatar,
              nickname: userInfo.nickname,
            });
          }
        }
      },
      deep: true,
      immediate: true
    }
  }
  };
  </script>
  
  <style scoped>

  .coze-custom-float-btn {
    position: relative; /* 关键：让提示文字相对悬浮球定位 */
    cursor: pointer;
  
    &:hover {
      transform: scale(1.05);
      background: #40a9ff !important;
    }
  
    &:hover .float-tooltip {
      /* 鼠标悬浮时显示文字 */
      opacity: 1;
      visibility: visible;
    }
  
    &.active {
      background: #096dd9 !important;
    }
  }
  
  /* 图标样式（默认状态） */
  .coze-float-icon {
    display: block;
    width: 30px;
    height: 30px;
    background-image: url("../assets/coze1.png"); /* 默认图片 */
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transition: background-image 0.2s ease; /* 平滑过渡 */
  }
  
  /* 鼠标悬浮时 → 切换图片 */
  .coze-custom-float-btn:hover .coze-float-icon {
    background-image: url("../assets/coze.png"); /* 悬浮图片 */
  }
  
  /* 悬浮提示文字样式 */
  .float-tooltip {
    position: absolute;
    right: 115%; /* 显示在悬浮球左侧 */
    top: 50%;
    transform: translateY(-50%);
    padding: 4px 10px;
    background: #333;
    color: #fff;
    font-size: 12px;
    border-radius: 4px;
    white-space: nowrap;
    z-index: 1;
    
    /* 默认隐藏 */
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
  }
  
  /* 悬浮提示文字样式 —— 正上方居中 */
  .float-tooltip-up {
    position: absolute;
    /* 👇 核心：正上方居中 */
    bottom: 100%;    /* 贴在悬浮球顶部 */
    left: 50%;       /* 水平居中基准 */
    transform: translateX(-50%); /* 真正居中 */
    
    margin-bottom: 6px; /* 离悬浮球一点距离，更好看 */
    padding: 4px 10px;
    background: #333;
    color: #fff;
    font-size: 12px;
    border-radius: 4px;
    white-space: nowrap;
    z-index: 1;
    
    /* 默认隐藏 */
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
  }
  
</style>
  